import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {
  CancelSessionPayload,
  ICancelSessionRes,
  ICreateOrUpdateSessionPayload,
  IDefaultSession,
  IExperienceSessionsParams,
  IPaidSessionPayload,
  ISessionByIdDetails,
  ISessionLearnerItem,
  ISessionLearnersRes,
  ISessionListItem,
  ListResponse,
  LogsItem,
} from '@/models';
import {getDiffBetweenTwoDatesInDays, http} from '@/utils';
import {handleApiCallInStore, updateItemById} from './utils';

import {create} from 'zustand';
import {message} from 'antd';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface ICreateOrEditSessionParams {
  expId: string;
  sessionId?: string;
  payload: ICreateOrUpdateSessionPayload;
  onSuccess: () => void;
}

export interface ISessionsStore {
  nbHits: number;
  experienceSessions: ISessionListItem[];
  hasUnpaidClosedSessions: boolean;
  sessionsLogs: LogsItem[];
  sessionLogs: LogsItem[];
  sessionLearnersLogs: LogsItem[];
  defaultSession: IDefaultSession;
  sessionDetails: ISessionByIdDetails;
  sessionLearners: ISessionLearnersRes;

  getExperienceSessions: (params: IExperienceSessionsParams) => void;
  handlePaidChange: (payload: IPaidSessionPayload) => void;
  cancelSession: (payload: CancelSessionPayload) => void;
  getSessionsLogs: (expId: string) => void;
  getSessionLogs: (sessionId: string) => void;

  getDefaultSession: (
    expId: string,
    onSuccess: (data: IDefaultSession) => void
  ) => void;
  getSessionDataById: (
    expId: string,
    sessionId: string,
    onSuccess: (data: ISessionByIdDetails) => void
  ) => void;
  getSessionLearners: (
    expId: string,
    sessionId: string
    // onSuccess: (data: ISessionLearnersRes) => void
  ) => void;
  resetSession: (expId: string, sessionId: string) => void;
  getSessionLearnersLogs: (sessionId: string) => void;
  createOrUpdateSession: (params: ICreateOrEditSessionParams) => void;
  removeSessionLearner: (
    expId: string,
    sessionId: string,
    learnerId: string
  ) => void;
  putAttendees: (
    expId: string,
    sessionId: string,
    attendees: ISessionLearnerItem[]
  ) => void;
  changeLearnerStatus: (isAbsent: boolean, learnerId: string) => void;
}

const fetchSessionLearners = async (
  expId: string,
  sessionId: string,
  onSuccess: (data: ISessionLearnersRes) => void
) => {
  const {data} = await http.get<ISessionLearnersRes>(
    API_ENDPOINTS.sessions.sessionLearners(expId, sessionId)
  );
  onSuccess(data);
};

export const useSessionsStore = create(
  persist<ISessionsStore>(
    (set, get) => ({
      nbHits: 0,
      sessionLogs: [],
      defaultSession: undefined,
      hasUnpaidClosedSessions: false,
      experienceSessions: [],
      sessionsLogs: [],
      sessionDetails: undefined,
      sessionLearners: undefined,
      sessionLearnersLogs: [],
      getExperienceSessions: params => {
        handleApiCallInStore(LoadingKeys.ExperienceSessions, async () => {
          const {id, ...otherParams} = params;
          const {data} = await http.get<ListResponse<ISessionListItem>>(
            `${API_ENDPOINTS.experiences.all}/${id}/sessions`,
            {params: {...otherParams}} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          const unpaidClosedSessions = items.filter(
            item =>
              !item.paid &&
              item.status === 'closed' &&
              getDiffBetweenTwoDatesInDays(item.endDate) >= 8
          );

          set(() => ({
            experienceSessions: items ?? [],
            nbHits,
            hasUnpaidClosedSessions: unpaidClosedSessions?.length > 0,
          }));
        });
      },

      handlePaidChange: payload => {
        handleApiCallInStore(LoadingKeys.SessionPaidChange, async () => {
          const {objectID, expId, paid} = payload;
          await http.put(
            `${API_ENDPOINTS.experiences.all}/${expId}/sessions/${objectID}/paid`,
            {paid}
          );
        });
      },
      cancelSession: payload => {
        handleApiCallInStore(LoadingKeys.CancelSession, async () => {
          const {objectID, expId} = payload;
          const {data} = await http.put<
            { status: 'canceled' },
            AxiosResponse<ICancelSessionRes>
          >(
            `${API_ENDPOINTS.experiences.all}/${expId}/sessions/${objectID}/status`,
            {status: 'canceled'}
          );
          if (data?.code === 'REFUND_ERROR') {
            if (typeof data?.message === 'object') {
              message.success(data?.message?.ar);
            }
          } else {
            message.success(t('refundSuccess'));
          }
          message.success(t('sessionCanceledSuccessfully'));
          set(({experienceSessions}) => ({
            experienceSessions: updateItemById(
              experienceSessions,
              'status',
              'canceled',
              objectID
            ),
          }));
        });
      },
      getSessionsLogs: expId => {
        handleApiCallInStore(LoadingKeys.SessionsLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            `${API_ENDPOINTS.logs}${API_ENDPOINTS.sessions.all}/paid?id=${expId}`
          );
          set(() => ({sessionsLogs: data?.items}));
        });
      },
      getSessionLogs: sessionId => {
        handleApiCallInStore(LoadingKeys.SessionLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            `${API_ENDPOINTS.logs}${API_ENDPOINTS.sessions.all}?id=${sessionId}`
          );
          set(() => ({sessionLogs: data?.items}));
        });
      },
      getDefaultSession: (expId, onSuccess) => {
        handleApiCallInStore(LoadingKeys.DefaultSession, async () => {
          const {data} = await http.get<IDefaultSession>(
            `${API_ENDPOINTS.experiences.all}/${expId}/${API_ENDPOINTS.sessions.default}`
          );
          onSuccess(data);
          set(() => ({defaultSession: data}));
        });
      },
      removeSessionLearner: (expId, sessionId, learnerId) => {
        handleApiCallInStore(LoadingKeys.RemoveSessionLearner, async () => {
          const {data} = await http.put<{
            code: string;
            message: { ar: string; en: string };
          }>(API_ENDPOINTS.sessions.removeLearner(expId, sessionId, learnerId));
          message.success(t('theLearnerRemovedSuccessfully'));
          if (data?.code === 'REFUND_ERROR') {
            message.success(
              data?.message?.ar ?? 'somethingWentWrongDuringRefund'
            );
          } else {
            message.success(t('TheAmountHasBeenSuccessfullyRefunded'));
          }
          await fetchSessionLearners(expId, sessionId, data =>
            set(() => ({sessionLearners: data}))
          );
        });
      },
      getSessionDataById: (expId, sessionId, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetSessionDetailsById, async () => {
          const {data} = await http.get<ISessionByIdDetails>(
            `${API_ENDPOINTS.experiences.all}/${expId}${API_ENDPOINTS.sessions.all}/${sessionId}`
          );
          onSuccess(data);
          set(() => ({sessionDetails: data}));
        });
      },
      getSessionLearners: (expId, sessionId) => {
        handleApiCallInStore(LoadingKeys.GetSessionLearners, async () => {
          const {data} = await http.get<ISessionLearnersRes>(
            API_ENDPOINTS.sessions.sessionLearners(expId, sessionId)
          );
          set(() => ({sessionLearners: data}));
        });
      },
      changeLearnerStatus: (isAbsent, learnerId) => {
        const {sessionLearners} = get();
        const newSessionLearners = {
          ...sessionLearners,
          attendees: sessionLearners?.attendees.map(item => {
            if (item.attendeesID === learnerId) {
              return {...item, isAbsent};
            }
            return item;
          }),
        };
        set(() => ({
          sessionLearners: newSessionLearners,
        }));
      },
      getSessionLearnersLogs: sessionId => {
        handleApiCallInStore(LoadingKeys.GetSessionLearnersLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            API_ENDPOINTS.sessions.sessionLearnersLogs(sessionId)
          );
          set(() => ({sessionLearnersLogs: data?.items}));
        });
      },
      createOrUpdateSession: ({expId, sessionId, onSuccess, payload}) => {
        handleApiCallInStore(LoadingKeys.CreateOrEditSession, async () => {
          const apiLink = `${API_ENDPOINTS.experiences.all}/${expId}${
            API_ENDPOINTS.sessions.all
          }${sessionId ? `/${sessionId}` : ''}`;
          if (sessionId) {
            await http.put(apiLink, payload);
          } else {
            await http.post(apiLink, payload);
          }
          await onSuccess();
        });
      },
      putAttendees: (expId, sessionId, attendees) => {
        handleApiCallInStore(LoadingKeys.PutAttendees, async () => {
          await http.put<{ attendees: ISessionLearnerItem[] }>(
            API_ENDPOINTS.sessions.putAttendees(expId, sessionId),
            {attendees}
          );
          await fetchSessionLearners(expId, sessionId, data =>
            set(() => ({sessionLearners: data}))
          );
        });
      },
      resetSession: (expId: string, sessionId: string) => {
        handleApiCallInStore(LoadingKeys.ResetSession, async () => {
          await fetchSessionLearners(expId, sessionId, data =>
            set(() => ({sessionLearners: data}))
          );
        });
      },
    }),
    {
      name: 'sessions-storage',

    }
  )
);
